import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Creator } from '~/clientApi/creator.model';
import { USER_ROLES_API_MGMNT } from '~/constants';
import { CREATOR_MANAGER_RELATION } from '~/constants/creatorManagerRelation';
import { selectTalentManager } from '~/selectors/talent-manager';
import { CreatorType, ICreator } from '~/types';
import { MeResponse } from '~/userManagementApi/userAccess.types';
import { getMainRoleUserMgnmtApi } from '~/userManagementApi/utils/getMainRoleUserMgnmtApi';

import { useCurrentUserCreatorRelation } from './useCurrentUserCreatorRelation';

// reusable, extensible hook for creating spec. variables that represents the access and permissions of a (co)manager or admin
// according to the access and permissions excel file (https://docs.google.com/spreadsheets/d/1IzAyLFAAXnyAFjT7qSFujqH59YbpWG9LRRYCQtcaYUw)
export const useTalentManagerAccess = (
  creator: ICreator | Creator | CreatorType,
  userAccessData: MeResponse,
) => {
  const relationToCreator = useCurrentUserCreatorRelation(creator);
  const manager = useSelector(selectTalentManager);

  const isOwnAgencyAdmin =
    relationToCreator === CREATOR_MANAGER_RELATION.inOwnAgencyTeam && // isInOwnAgencyTeam
    getMainRoleUserMgnmtApi(userAccessData) ===
      USER_ROLES_API_MGMNT.agencyAdmin; // isAgencyAdmin

  // access for manager, co-manager, and external co-manager
  const shouldHaveAccessAsManagerComanager = useMemo(() => {
    return (
      manager === null ||
      [
        CREATOR_MANAGER_RELATION.manager,
        CREATOR_MANAGER_RELATION.coManager,
        CREATOR_MANAGER_RELATION.externalCoManager,
      ].includes(relationToCreator as string)
    );
  }, [relationToCreator, manager]);

  // access for manager, co-manager, external co-manager, and agency admin
  const shouldHaveAccessAsManagerComanagerAdmin = useMemo(() => {
    return shouldHaveAccessAsManagerComanager || isOwnAgencyAdmin;
  }, [shouldHaveAccessAsManagerComanager, isOwnAgencyAdmin]);

  return {
    isOwnAgencyAdmin,
    shouldHaveAccessAsManagerComanager,
    shouldHaveAccessAsManagerComanagerAdmin,
  };
};
